/**
 * section-custom-product-page.js
 * Handles: carousel, thumbnails, expand/collapse, flavor tabs, FAQ accordion,
 *          CTA scroll, add-to-cart, floating add-to-cart bar
 * Vanilla JS, custom elements pattern, multi-instance safe
 */

(function () {
  'use strict';

  /* ── Utility: slide toggle (CSS height animation) ─────── */
  function slideOpen(el) {
    el.removeAttribute('hidden');
    el.style.height = '0';
    el.style.overflow = 'hidden';
    el.style.transition = 'height 0.28s ease';
    requestAnimationFrame(() => {
      el.style.height = el.scrollHeight + 'px';
    });
    el.addEventListener('transitionend', function handler() {
      el.style.height = '';
      el.style.overflow = '';
      el.style.transition = '';
      el.removeEventListener('transitionend', handler);
    });
  }

  function slideClose(el, callback) {
    el.style.height = el.scrollHeight + 'px';
    el.style.overflow = 'hidden';
    el.style.transition = 'height 0.25s ease';
    requestAnimationFrame(() => {
      el.style.height = '0';
    });
    el.addEventListener('transitionend', function handler() {
      el.setAttribute('hidden', '');
      el.style.height = '';
      el.style.overflow = '';
      el.style.transition = '';
      el.removeEventListener('transitionend', handler);
      if (callback) callback();
    });
  }

  /* ── Carousel ─────────────────────────────────────────── */
  function initCarousel(root) {
    const carousel = root.querySelector('[data-carousel]');
    if (!carousel) return;

    const slides = carousel.querySelectorAll('[data-slide]');
    const thumbs = carousel.querySelectorAll('[data-thumb]');
    const prevBtn = carousel.querySelector('[data-carousel-prev]');
    const nextBtn = carousel.querySelector('[data-carousel-next]');
    const thumbsWrapper = carousel.querySelector('.cpp__thumbnails-wrapper');
    const track = carousel.querySelector('[data-carousel-track]');

    if (!slides.length) return;

    const total = slides.length;
    let current = 0;

    // Below 1024px the stylesheet turns the track into a scroll-snap container,
    // so the slides sit side by side and the finger drags them natively. Above
    // it they stack and crossfade. Asking the element whether it can scroll
    // keeps this in step with the CSS without duplicating the breakpoint.
    function isDragMode() {
      return !!track && track.scrollWidth - track.clientWidth > 1;
    }

    function scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    function updateArrows() {
      if (prevBtn) {
        if (current === 0) {
          prevBtn.classList.add('is-hidden');
        } else {
          prevBtn.classList.remove('is-hidden');
        }
      }
      if (nextBtn) {
        if (current === total - 1) {
          nextBtn.classList.add('is-hidden');
        } else {
          nextBtn.classList.remove('is-hidden');
        }
      }
    }

    // Marks a slide current: classes, thumbnail rail, arrows. It never moves the
    // main image, so the scroll listener below can call it mid-drag without
    // fighting the finger.
    function setActive(index) {
      if (index === current || index < 0 || index > total - 1) return;

      // A disclosure belongs to one image. The moment another one is in front,
      // the open bubble goes with it.
      closeDisclosures(carousel);

      // Deactivate old
      slides[current].classList.remove('is-active');
      if (thumbs[current]) thumbs[current].classList.remove('is-active');

      // Activate new
      current = index;
      slides[current].classList.add('is-active');
      if (thumbs[current]) {
        thumbs[current].classList.add('is-active');
        // Scroll thumb into view within wrapper
        if (thumbsWrapper) {
          const thumb = thumbs[current];
          const wrapLeft = thumbsWrapper.scrollLeft;
          const wrapRight = wrapLeft + thumbsWrapper.offsetWidth;
          const thumbLeft = thumb.offsetLeft;
          const thumbRight = thumbLeft + thumb.offsetWidth;
          if (thumbLeft < wrapLeft) {
            thumbsWrapper.scrollTo({ left: thumbLeft - 8, behavior: scrollBehavior() });
          } else if (thumbRight > wrapRight) {
            thumbsWrapper.scrollTo({ left: thumbRight - thumbsWrapper.offsetWidth + 8, behavior: scrollBehavior() });
          }
        }
      }

      updateArrows();
    }

    function goTo(index) {
      // Clamp to valid range (no wrap-around)
      index = Math.max(0, Math.min(index, total - 1));

      // In drag mode the arrows, the thumbnails and the theme editor all steer
      // by scrolling the track; the scroll listener then confirms the landing.
      if (isDragMode()) {
        track.scrollTo({ left: index * track.clientWidth, behavior: scrollBehavior() });
        setActive(index);
        return;
      }

      setActive(index);
    }

    // Exposed for the theme editor's block-select handler at the bottom of this
    // file, which has to reach into a specific carousel to bring a slide forward.
    carousel.cppGoTo = goTo;

    if (prevBtn) prevBtn.addEventListener('click', () => goTo(current - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => goTo(current + 1));

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        goTo(parseInt(thumb.dataset.thumb, 10));
      });
    });

    if (track) {
      // Read the resting slide back off the scroll position. Throttled to one
      // frame so a fling does not run this per scroll event.
      let syncFrame = 0;
      track.addEventListener('scroll', () => {
        if (syncFrame) return;
        syncFrame = requestAnimationFrame(() => {
          syncFrame = 0;
          const width = track.clientWidth;
          if (!width || !isDragMode()) return;
          setActive(Math.round(track.scrollLeft / width));
        });
      }, { passive: true });

      // Stacked, the lazy slides were all inside the viewport and loaded anyway.
      // Side by side they are not, so the first touch pulls them in before the
      // drag can reach a slide that has nothing painted yet.
      const primeImages = () => {
        track.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      };
      track.addEventListener('touchstart', primeImages, { passive: true, once: true });

      // A rotation or a crossing of the breakpoint leaves the scroll offset on
      // the old slide width; put the current slide back under the frame.
      let resizeTimer = 0;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          if (isDragMode()) track.scrollLeft = current * track.clientWidth;
        }, 120);
      });
    }

    // Touch/swipe on the main image, for the crossfade mode only — in drag mode
    // the native scroller owns the gesture.
    const mainEl = carousel.querySelector('.cpp__carousel-main');
    if (mainEl) {
      let startX = 0;
      mainEl.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      mainEl.addEventListener('touchend', (e) => {
        if (isDragMode()) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) goTo(dx < 0 ? current + 1 : current - 1);
      }, { passive: true });
    }

    // Set initial arrow state
    updateArrows();
  }

  /* ── Per-image disclosures ───────────────────────────── */
  // Module scope so the carousel can shut an open bubble the instant its slide
  // stops being the one on screen — a footnote must never outlive its image.
  function closeDisclosures(scope) {
    if (!scope) return;
    scope.querySelectorAll('[data-disc].is-open').forEach((disc) => {
      disc.classList.remove('is-open');
      const chip = disc.querySelector('[data-disc-chip]');
      if (chip) chip.setAttribute('aria-expanded', 'false');
    });
  }

  function initDisclosures(root) {
    const discs = root.querySelectorAll('[data-disc]');
    if (!discs.length) return;

    // Hover is a convenience for a mouse. Gating on the media query rather
    // than on a touch sniff keeps a laptop with a touchscreen sane, and stops
    // a phone from spending its first tap satisfying a hover state.
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    // Tried in order before a scrollbar is admitted. Each is only used if it
    // is genuinely smaller than what the merchant asked for, so a section set
    // to 10px never gets inflated to 11 by the fitter.
    const STEPS = [11, 10.5, 10];

    discs.forEach((disc) => {
      const chip = disc.querySelector('[data-disc-chip]');
      const bubble = disc.querySelector('[data-disc-bubble]');
      const scroll = disc.querySelector('[data-disc-scroll]');
      const firstNote = disc.querySelector('.cpp__disc-note');
      if (!chip || !bubble || !scroll) return;

      let openTimer = 0;
      let closeTimer = 0;
      let holdTimer = 0;
      let autoTimer = 0;
      let holdFired = false;
      let moved = false;
      let touchHandled = false;
      let startX = 0;
      let startY = 0;

      // How long a tap leaves the bubble up. A hold passes nothing and gets no
      // timer at all, so it stays until something closes it.
      const TAP_MS = parseInt(disc.dataset.discTimeout, 10) || 5000;

      function overflows() {
        return scroll.scrollHeight > scroll.clientHeight + 1;
      }

      // Long notes lose a point or two of type before they lose the ability to
      // be read at a glance; past the floor the bubble scrolls instead. Run on
      // open rather than on load, because a hidden bubble has no measurements.
      function fit() {
        bubble.style.removeProperty('--cpp-disc-font-size');
        disc.classList.remove('is-scrollable', 'is-at-end');

        const base = firstNote ? parseFloat(getComputedStyle(firstNote).fontSize) : 0;
        STEPS.forEach((px) => {
          if (!overflows() || !(px < base)) return;
          bubble.style.setProperty('--cpp-disc-font-size', px + 'px');
        });

        if (overflows()) {
          disc.classList.add('is-scrollable');
          scroll.scrollTop = 0;
        }
        markEnd();
      }

      function markEnd() {
        disc.classList.toggle(
          'is-at-end',
          scroll.scrollTop + scroll.clientHeight >= scroll.scrollHeight - 2
        );
      }

      // Called with a duration by a tap, and bare by a hold, a hover and the
      // keyboard — those leave it up until something else takes it down.
      function open(autoMs) {
        if (!disc.classList.contains('is-open')) {
          // One at a time, page-wide. Two open bubbles are never useful.
          closeDisclosures(document);
          disc.classList.add('is-open');
          chip.setAttribute('aria-expanded', 'true');
          fit();
        }
        clearTimeout(autoTimer);
        if (autoMs) autoTimer = setTimeout(close, autoMs);
      }

      function close() {
        clearTimeout(autoTimer);
        if (!disc.classList.contains('is-open')) return;
        disc.classList.remove('is-open');
        chip.setAttribute('aria-expanded', 'false');
      }

      scroll.addEventListener('scroll', markEnd, { passive: true });

      // Reaching into the bubble means the five seconds were not enough. Taking
      // it away mid-sentence would be the worst moment to.
      bubble.addEventListener('touchstart', () => { clearTimeout(autoTimer); }, { passive: true });

      if (canHover) {
        // The bubble is part of the hover target, so the pointer can travel
        // into it to scroll; the grace period covers the gap it crosses.
        const enter = () => {
          clearTimeout(closeTimer);
          clearTimeout(openTimer);
          openTimer = setTimeout(open, 120);
        };
        const leave = () => {
          clearTimeout(openTimer);
          clearTimeout(closeTimer);
          closeTimer = setTimeout(close, 180);
        };
        chip.addEventListener('mouseenter', enter);
        chip.addEventListener('mouseleave', leave);
        bubble.addEventListener('mouseenter', enter);
        bubble.addEventListener('mouseleave', leave);
      }

      // Touch has two gestures. A tap shows the note for a few seconds and then
      // takes it away; holding past 300ms keeps it up. A drag that turns into a
      // swipe is neither, so starting a gesture on the marker still flicks to
      // the next image.
      chip.addEventListener('touchstart', (e) => {
        const t = e.touches[0];
        startX = t.clientX;
        startY = t.clientY;
        holdFired = false;
        moved = false;
        clearTimeout(holdTimer);
        holdTimer = setTimeout(() => {
          holdFired = true;
          open();
        }, 300);
      }, { passive: true });

      chip.addEventListener('touchmove', (e) => {
        const t = e.touches[0];
        if (Math.abs(t.clientX - startX) > 8 || Math.abs(t.clientY - startY) > 8) {
          moved = true;
          clearTimeout(holdTimer);
        }
      }, { passive: true });

      chip.addEventListener('touchend', () => {
        clearTimeout(holdTimer);
        // Whatever the touch decided, the click trailing it must not undo it.
        touchHandled = true;
        setTimeout(() => { touchHandled = false; }, 500);

        // The hold has already opened it, and a swipe was never a tap.
        if (holdFired || moved) return;

        if (disc.classList.contains('is-open')) close(); else open(TAP_MS);
      }, { passive: true });

      chip.addEventListener('touchcancel', () => {
        clearTimeout(holdTimer);
      }, { passive: true });

      chip.addEventListener('click', (e) => {
        e.preventDefault();
        if (touchHandled) return;
        // Enter and Space arrive here with no pointer behind them. A mouse
        // click is left alone — hover governs on a pointer device.
        if (e.detail === 0) {
          if (disc.classList.contains('is-open')) close(); else open();
        }
      });

      // Keyboard focus opens it; a tap that merely focuses the button must not.
      disc.addEventListener('focusin', () => {
        try {
          if (chip.matches(':focus-visible')) open();
        } catch (err) {
          /* older browsers: keyboard users still have Enter */
        }
      });
      disc.addEventListener('focusout', (e) => {
        if (!disc.contains(e.relatedTarget)) close();
      });

      // A rotation changes how much fits, so an open bubble is re-measured.
      let fitTimer = 0;
      window.addEventListener('resize', () => {
        if (!disc.classList.contains('is-open')) return;
        clearTimeout(fitTimer);
        fitTimer = setTimeout(fit, 120);
      });

      // For the theme editor handler at the bottom of this file.
      disc.cppOpenDisc = open;
    });

    // Dismissal is page-wide, so it is wired once however many sections there
    // are. Capture, because a bubble can sit over other clickable furniture.
    if (!initDisclosures.bound) {
      initDisclosures.bound = true;

      document.addEventListener('pointerdown', (e) => {
        if (e.target.closest && e.target.closest('[data-disc]')) return;
        closeDisclosures(document);
      }, true);

      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const openDisc = document.querySelector('[data-disc].is-open');
        if (!openDisc) return;
        closeDisclosures(document);
        const chip = openDisc.querySelector('[data-disc-chip]');
        if (chip) chip.focus();
      });
    }
  }

  /* ── Expand / Collapse (Nutrition & Ingredients) ─────── */
  function initExpand(root) {
    root.querySelectorAll('[data-expand]').forEach((expand) => {
      const toggle = expand.querySelector('[data-expand-toggle]');
      const body = expand.querySelector('[data-expand-body]');
      if (!toggle || !body) return;

      toggle.addEventListener('click', () => {
        const isOpen = expand.hasAttribute('data-open');
        if (isOpen) {
          expand.removeAttribute('data-open');
          toggle.setAttribute('aria-expanded', 'false');
          slideClose(body);
        } else {
          expand.setAttribute('data-open', '');
          toggle.setAttribute('aria-expanded', 'true');
          slideOpen(body);
        }
      });
    });
  }

  /* ── Flavor Tabs ──────────────────────────────────────── */
  function initFlavorTabs(root) {
    const tabLists = root.querySelectorAll('.cpp__flavor-tabs');
    tabLists.forEach((tabList) => {
      const tabs = tabList.querySelectorAll('[data-flavor-tab]');
      const expandEl = tabList.closest('[data-expand]');
      if (!expandEl) return;
      const panels = expandEl.querySelectorAll('[data-flavor-panel]');

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const idx = parseInt(tab.dataset.flavorTab, 10);
          // Deactivate all
          tabs.forEach((t) => { t.classList.remove('is-active'); t.setAttribute('aria-selected', 'false'); });
          panels.forEach((p) => { p.classList.remove('is-active'); p.setAttribute('hidden', ''); });
          // Activate selected
          tab.classList.add('is-active');
          tab.setAttribute('aria-selected', 'true');
          const targetPanel = expandEl.querySelector(`[data-flavor-panel="${idx}"]`);
          if (targetPanel) {
            targetPanel.classList.add('is-active');
            targetPanel.removeAttribute('hidden');
          }
        });
      });
    });
  }

  /* ── FAQ Accordion (one open at a time) ──────────────── */
  function initFAQ(root) {
    root.querySelectorAll('.cpp__faq').forEach((faq) => {
      const items = faq.querySelectorAll('[data-faq-item]');

      function closeItem(item) {
        const answer = item.querySelector('[data-faq-answer]');
        const trigger = item.querySelector('[data-faq-trigger]');
        if (!answer || !item.classList.contains('is-open')) return;
        item.classList.remove('is-open');
        trigger && trigger.setAttribute('aria-expanded', 'false');
        slideClose(answer);
      }

      items.forEach((item) => {
        const trigger = item.querySelector('[data-faq-trigger]');
        const answer = item.querySelector('[data-faq-answer]');
        if (!trigger || !answer) return;

        trigger.addEventListener('click', () => {
          const isOpen = item.classList.contains('is-open');
          // Close all
          items.forEach((other) => { if (other !== item) closeItem(other); });
          if (isOpen) {
            closeItem(item);
          } else {
            item.classList.add('is-open');
            trigger.setAttribute('aria-expanded', 'true');
            slideOpen(answer);
          }
        });
      });
    });
  }

  /* ── CTA Button: scroll-to support ───────────────────── */
  function initCTAScroll(root) {
    root.querySelectorAll('[data-cpp-btn-scroll]').forEach((btn) => {
      const selector = btn.dataset.cppBtnScroll;
      if (!selector) return;

      btn.addEventListener('click', () => {
        const target = document.querySelector(selector);
        if (!target) return;

        // Offset for any sticky headers (reads common theme header heights)
        const stickyHeader = document.querySelector(
          'sticky-header, .shopify-section-header-sticky, header.shopify-section, #shopify-section-header'
        );
        const offset = stickyHeader ? stickyHeader.offsetHeight : 0;

        const targetTop = target.getBoundingClientRect().top + window.scrollY - offset - 16;
        window.scrollTo({ top: targetTop, behavior: 'instant' });
      });
    });
  }

  /* ── Add to Cart button: ajax add + slide-in cart ───── */
  //
  // Mirrors the theme's own add flow: we ask /cart/add.js for the section
  // markup Eurus considers cart UI (via its xCartHelper store), swap that in,
  // then open the drawer. If Alpine has not booted — or the drawer is off —
  // we fall back to /cart so the shopper always sees the add land.
  function alpineStore(name) {
    try {
      if (window.Alpine && typeof Alpine.store === 'function') return Alpine.store(name);
    } catch (e) { /* Alpine not ready */ }
    return null;
  }

  function sectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : null;
  }

  function afterAdd(response) {
    const helper = alpineStore('xCartHelper');
    const miniCart = alpineStore('xMiniCart');

    if (helper && typeof helper.getSectionsToRender === 'function' && response && response.sections) {
      helper.getSectionsToRender().forEach((section) => {
        section.selector.split(',').forEach((selector) => {
          const host = document.querySelector(selector);
          if (!host || !response.sections[section.id]) return;
          const markup = sectionInnerHTML(response.sections[section.id], selector);
          if (markup !== null) host.innerHTML = markup;
        });
      });
    }

    if (helper && typeof helper.updateEstimateShippingFull === 'function') {
      helper.updateEstimateShippingFull();
    }

    document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));

    if (miniCart && typeof miniCart.openCart === 'function') {
      miniCart.openCart();
    } else {
      window.location.href = '/cart';
    }
  }

  function addToCart(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId || btn.classList.contains('is-loading')) return;

    const line = { id: Number(variantId), quantity: Number(btn.dataset.quantity) || 1 };
    // /cart/add.js wants the key left off entirely for a one-time purchase.
    if (btn.dataset.sellingPlan) line.selling_plan = Number(btn.dataset.sellingPlan);

    const body = { items: [line] };
    const helper = alpineStore('xCartHelper');
    if (helper && typeof helper.getSectionsToRender === 'function') {
      body.sections = helper.getSectionsToRender().map((s) => s.id).join(',');
    }

    btn.classList.add('is-loading');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then((r) => r.json().then((data) => ({ ok: r.ok, data: data })))
      .then((res) => {
        if (!res.ok) throw new Error((res.data && res.data.description) || 'Add to cart failed');
        afterAdd(res.data);
      })
      .catch((err) => {
        console.error('[cpp:atc]', err);
        window.location.href = '/cart';
      })
      .then(() => { btn.classList.remove('is-loading'); });
  }

  // Fire on pointerup (the real touch release) as well as click. The theme
  // defers its JS to the first touch and can swallow the click synthesized
  // from it, and the floating bar repositions on visualViewport changes
  // which drops the click too — either way the first tap looks dead. The
  // handler therefore has to be safe to run twice for one tap.
  function bindTap(el, handler) {
    let px = 0, py = 0, moved = false;
    el.addEventListener('pointerdown', (e) => { px = e.clientX; py = e.clientY; moved = false; }, { passive: true });
    el.addEventListener('pointermove', (e) => {
      if (Math.abs(e.clientX - px) > 10 || Math.abs(e.clientY - py) > 10) moved = true;
    }, { passive: true });
    el.addEventListener('pointerup', (e) => {
      if (e.pointerType === 'mouse') return;  // desktop: let the click through
      if (moved) return;                      // was a scroll/drag, not a tap
      handler();
    });
    el.addEventListener('click', (event) => {
      event.preventDefault();
      handler();
    });
  }

  // A touch tap fires pointerup and then, up to ~300ms later, a click that the
  // browser hit-tests afresh. Anything that has appeared under the finger in
  // between receives it — for the swap sheet that is the scrim, or worse, one
  // of its add buttons where the floating bar sits at the bottom of the
  // screen. Swallowing at the capture phase stops it reaching any of them.
  function swallowNextClick(ms) {
    const stop = (event) => {
      event.stopPropagation();
      event.preventDefault();
    };
    document.addEventListener('click', stop, true);
    setTimeout(() => { document.removeEventListener('click', stop, true); }, ms || 350);
  }

  function initATC(root) {
    root.querySelectorAll('[data-cpp-atc]').forEach((btn) => {
      if (btn.dataset.cppAtcReady === '1') return;
      btn.dataset.cppAtcReady = '1';
      // Safe to run twice: addToCart() bails while is-loading is set.
      bindTap(btn, () => addToCart(btn));
    });
  }

  /* ── Swap sheet: offer an alternative before adding ──── */
  //
  // The trigger adds nothing itself — it opens a bottom sheet holding one
  // alternative product. Both buttons inside the sheet are ordinary
  // [data-cpp-atc] buttons, so initATC wires the real add and afterAdd opens
  // the drawer as usual; all this does is show and hide the sheet.
  function initSwap(root) {
    root.querySelectorAll('[data-cpp-swap]').forEach((trigger) => {
      if (trigger.dataset.cppSwapReady === '1') return;
      trigger.dataset.cppSwapReady = '1';

      const sheet = document.getElementById(trigger.dataset.cppSwap);
      if (!sheet) return;

      let lastFocus = null;
      let restoreOverflow = '';
      // See open(): both guard against the ghost click that follows a tap.
      let suppressCloseUntil = 0;
      let lastPointerType = '';

      // Registered before bindTap's own pointerup, so this has already
      // recorded the pointer type by the time open() runs.
      trigger.addEventListener('pointerup', (e) => {
        lastPointerType = e.pointerType;
      }, { passive: true });

      function onKeydown(event) {
        if (event.key === 'Escape') close();
      }

      function open() {
        if (sheet.classList.contains('is-open')) return;
        lastFocus = document.activeElement;
        restoreOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        sheet.classList.add('is-open');
        sheet.setAttribute('aria-hidden', 'false');
        document.addEventListener('keydown', onKeydown);

        // Opened by a tap: kill the ghost click before it reaches the sheet.
        // Mouse never needs this — bindTap ignores its pointerup, so on
        // desktop the sheet opens from the real click and nothing follows.
        if (lastPointerType && lastPointerType !== 'mouse') {
          swallowNextClick(350);
          suppressCloseUntil = Date.now() + 400;
        }

        // preventScroll: the handle sits at the bottom of the screen, and
        // focusing it would otherwise jump the page down on mobile.
        const handle = sheet.querySelector('.cpp__swap-handle');
        if (handle) handle.focus({ preventScroll: true });
      }

      function close() {
        if (!sheet.classList.contains('is-open')) return;
        sheet.classList.remove('is-open');
        sheet.setAttribute('aria-hidden', 'true');
        document.documentElement.style.overflow = restoreOverflow;
        document.removeEventListener('keydown', onKeydown);
        if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
      }

      bindTap(trigger, open);

      // Everything that dismisses the sheet — backdrop, handle, and both add
      // buttons on their way out — listens on pointerup as well as click, for
      // the same reason bindTap does. close() is idempotent, so a double fire
      // costs nothing, and no preventDefault here: the add buttons still need
      // their own click to reach initATC.
      function closeFromPointer() {
        if (Date.now() < suppressCloseUntil) return;
        close();
      }

      sheet.querySelectorAll('[data-cpp-swap-close]').forEach((el) => {
        el.addEventListener('click', closeFromPointer);
        el.addEventListener('pointerup', (e) => {
          if (e.pointerType !== 'mouse') closeFromPointer();
        });
      });
    });
  }

  /* ── Floating Add to Cart bar ────────────────────────── */
  //
  // Same trigger as the bundler's "next step" bar: slide up only once every
  // in-page CTA has scrolled out of view, so we never duplicate a button the
  // shopper can already see. The button inside carries data-cpp-atc, so
  // initATC above wires the actual add — this only governs visibility.
  function initATCSticky(root) {
    root.querySelectorAll('[data-cpp-sticky]').forEach((bar) => {
      if (bar.dataset.cppStickyReady === '1') return;
      bar.dataset.cppStickyReady = '1';

      if (!('IntersectionObserver' in window)) return;

      const visibleCtas = new Set();
      // The bundler runs its own sticky bar off this same trigger. Where both
      // are on the page we stand down, so the shopper sees one bar and it is
      // the one driving the bundle flow.
      const bundlerSticky = document.querySelector('.bundler__sticky');

      const update = () => {
        const deferring = !!bundlerSticky && bundlerSticky.classList.contains('is-visible');
        bar.classList.toggle('is-visible', visibleCtas.size === 0 && !deferring);
      };

      const ctas = document.querySelectorAll('.cpp__btn-wrap, [data-bundler-cta]');
      if (ctas.length) {
        const ctaObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) visibleCtas.add(entry.target);
            else visibleCtas.delete(entry.target);
          });
          update();
        }, { threshold: 0.1 });
        ctas.forEach((cta) => ctaObserver.observe(cta));
        // No initial update() — the observer fires straight after layout with
        // the true state, and calling it here would flash the bar open first.
      } else {
        // Nothing to hide behind, so the bar is simply always available.
        requestAnimationFrame(update);
      }

      if (bundlerSticky) {
        new MutationObserver(update).observe(bundlerSticky, {
          attributes: true, attributeFilter: ['class']
        });
      }

      // Pin to the bottom of the VISUAL viewport, not the layout viewport. On
      // iOS the layout viewport is sized as if the bottom toolbar were hidden,
      // so bottom:0 goes stale while that toolbar animates and the bar floats
      // above the true bottom edge.
      const vv = window.visualViewport;
      if (vv) {
        const positionBar = () => {
          const overlap = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
          bar.style.bottom = overlap + 'px';
        };
        let rafId = null;
        const onViewportChange = () => {
          if (rafId) cancelAnimationFrame(rafId);
          rafId = requestAnimationFrame(positionBar);
        };
        vv.addEventListener('resize', onViewportChange);
        vv.addEventListener('scroll', onViewportChange);
        positionBar();
      }
    });
  }

  /* ── Same-day shipping countdown ─────────────────────── */
  function initShippingCountdown(root) {
    const els = root.querySelectorAll('[data-cpp-countdown]');
    if (!els.length) return;

    // Seconds since midnight in a given IANA timezone. Re-read every
    // tick so DST transitions are handled automatically.
    function secondsInTz(tz) {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit'
      }).formatToParts(new Date());
      const o = {};
      parts.forEach((p) => { o[p.type] = p.value; });
      const h = parseInt(o.hour, 10) % 24; // '24' → 0 at midnight
      return h * 3600 + parseInt(o.minute, 10) * 60 + parseInt(o.second, 10);
    }

    function formatRemaining(totalMinutes) {
      const h = Math.floor(totalMinutes / 60);
      const m = totalMinutes % 60;
      if (h > 0) {
        return h + (h === 1 ? ' hr ' : ' hrs ') + m + (m === 1 ? ' min' : ' mins');
      }
      return m + (m === 1 ? ' min' : ' mins');
    }

    // Append text, wrapping any "*" in a faint/smaller footnote span.
    function appendText(parent, str) {
      const parts = String(str).split('*');
      parts.forEach((part, i) => {
        if (part) parent.appendChild(document.createTextNode(part));
        if (i < parts.length - 1) {
          const ast = document.createElement('span');
          ast.className = 'cpp__ship-ast';
          ast.textContent = '*';
          parent.appendChild(ast);
        }
      });
    }

    // Preview overrides so the badge can be seen on demand:
    //   • Theme editor (Shopify.designMode) → always shows a demo countdown.
    //   • Any storefront URL + ?ship_now=HH:MM → simulate the warehouse clock.
    const inEditor = !!(window.Shopify && window.Shopify.designMode);
    let previewSec = null;
    const qNow = new URLSearchParams(window.location.search).get('ship_now');
    if (qNow) {
      const m = qNow.match(/^(\d{1,2}):(\d{2})$/);
      if (m) {
        const ph = parseInt(m[1], 10);
        const pm = parseInt(m[2], 10);
        if (ph >= 0 && ph < 24 && pm >= 0 && pm < 60) previewSec = ph * 3600 + pm * 60;
      }
    }

    els.forEach((el) => {
      const tz = el.dataset.tz || 'America/Chicago';
      // Fulfillment hours (warehouse local): the message shows inside this
      // window and counts down to the stop time (= the same-day cutoff).
      let startHour = parseInt(el.dataset.start, 10);
      if (isNaN(startHour)) startHour = 0;
      let stopHour = parseInt(el.dataset.stop, 10);
      if (isNaN(stopHour)) stopHour = 24;
      // Only surface the countdown within this many hours of the stop time
      // (0 = show across the whole window). Keeps the urgency real.
      let withinHours = parseInt(el.dataset.within, 10);
      if (isNaN(withinHours)) withinHours = 0;
      const before = el.dataset.before || 'Order within {time} for same-day shipping';
      const fallback = el.dataset.fallback || '';
      const textEl = el.querySelector('.cpp__ship-text');
      if (!textEl) return;

      function paintCountdown(remMin) {
        const segments = before.split('{time}');
        textEl.textContent = '';
        appendText(textEl, segments[0] || '');
        const clock = document.createElement('span');
        clock.className = 'cpp__ship-clock';
        clock.textContent = formatRemaining(remMin);
        textEl.appendChild(clock);
        appendText(textEl, segments.slice(1).join('{time}') || '');
      }

      // Whenever the countdown can't run, show the plain delivery estimate
      // instead of nothing — the badge should never leave an empty slot in
      // the block stack. Only an empty fallback setting hides it outright.
      function showFallback() {
        if (!fallback) {
          el.hidden = true;
          return;
        }
        textEl.textContent = '';
        appendText(textEl, fallback);
        el.hidden = false;
      }

      function render() {
        const startSec = startHour * 3600;
        const stopSec = stopHour * 3600;

        // Editor demo: always show a representative countdown so the block
        // is visible and stylable regardless of the time-window settings.
        if (previewSec === null && inEditor) {
          paintCountdown(1 * 60 + 47); // 1h47m sample
          el.hidden = false;
          return;
        }

        let nowSec;
        if (previewSec !== null) {
          nowSec = previewSec;
        } else {
          try {
            nowSec = secondsInTz(tz);
          } catch (e) {
            // Invalid timezone string → no clock, so no countdown. The
            // fallback is time-independent, so it is still safe to show.
            showFallback();
            return;
          }
        }

        // Outside fulfillment hours (warehouse closed) → fallback.
        if (nowSec < startSec || nowSec >= stopSec) {
          showFallback();
          return;
        }

        // Inside hours but before the "show within N hours" lead-in: the
        // countdown would be too long to feel urgent, so show the fallback.
        const withinOk = withinHours <= 0 || (stopSec - nowSec) <= withinHours * 3600;
        if (!withinOk) {
          showFallback();
          return;
        }

        // Countdown active. Ceil so it never reads "0 mins" while seconds remain.
        paintCountdown(Math.ceil((stopSec - nowSec) / 60));
        el.hidden = false;
      }

      render();
      setInterval(render, 30000);
    });
  }

  /* ── Per-block desktop spacing swap ──────────────────── */
  function initBlockSpacing(root) {
    const mq = window.matchMedia('(min-width: 1024px)');
    const blocks = root.querySelectorAll('[data-spacing-desktop]');

    function apply() {
      blocks.forEach((el) => {
        const desktop = el.dataset.spacingDesktop;
        if (mq.matches) {
          el.style.marginTop = desktop + 'px';
        } else {
          const mobileVal = el.dataset.spacingMobile;
          if (mobileVal !== undefined) el.style.marginTop = mobileVal + 'px';
        }
      });
    }

    // Store mobile value as a data attribute so we can restore it
    blocks.forEach((el) => {
      el.dataset.spacingMobile = el.style.marginTop.replace('px', '');
    });

    apply();
    mq.addEventListener('change', apply);
  }

  /* ── Init all instances on page ──────────────────────── */
  function initSection(sectionEl) {
    initCarousel(sectionEl);
    initDisclosures(sectionEl);
    initExpand(sectionEl);
    initFlavorTabs(sectionEl);
    initFAQ(sectionEl);
    initCTAScroll(sectionEl);
    initATC(sectionEl);
    initSwap(sectionEl);
    initATCSticky(sectionEl);
    initBlockSpacing(sectionEl);
    initShippingCountdown(sectionEl);
  }

  function init() {
    document.querySelectorAll('.cpp').forEach(initSection);
  }

  // DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor live reload
  document.addEventListener('shopify:section:load', (e) => {
    const sectionEl = e.target.querySelector('.cpp');
    if (sectionEl) initSection(sectionEl);
  });

  // Three block types live inside the carousel rather than the content column:
  // the Gallery CTA Slide is a slide of its own, Image Badges sit on the first
  // one, and each Image Disclosure belongs to the image it names. All are
  // invisible unless their slide happens to be showing, so selecting one in
  // the theme editor brings its slide forward first.
  document.addEventListener('shopify:block:select', (e) => {
    const slide = e.target.closest && e.target.closest('[data-slide]');
    if (!slide) return;
    const carousel = slide.closest('[data-carousel]');
    if (!carousel || typeof carousel.cppGoTo !== 'function') return;
    carousel.cppGoTo(parseInt(slide.dataset.slide, 10));

    // A disclosure is a line of text inside a shut bubble, so the slide alone
    // shows the merchant nothing. Open it after the move — goTo closes bubbles
    // on its way past.
    const disc = e.target.closest('[data-disc]');
    if (disc && typeof disc.cppOpenDisc === 'function') disc.cppOpenDisc();
  });

  document.addEventListener('shopify:block:deselect', (e) => {
    const disc = e.target.closest && e.target.closest('[data-disc]');
    if (disc) closeDisclosures(disc.parentNode || document);
  });

})();